import type {
  AxiosInstance,
  AxiosResponse,
  RawAxiosRequestConfig,
} from "axios";
import { createRequestFunction } from "./common";

/**
 *
 * @export
 * @interface RequestArgs
 */
export interface RequestArgs {
  url: string;
  options: RawAxiosRequestConfig;
  hasFile?: boolean;
  displayError?: boolean;
}

/**
 *
 * @export
 * @enum {string}
 */
export enum RequestMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

/**
 *
 * @export
 * @class BaseAPI
 */
export class BaseAPI<D = {}, R = {}> {
  protected route = "";
  protected method: RequestMethod = RequestMethod.GET;
  protected params: string[] = [];
  protected hasFile = false;
  protected displayError = true;

  constructor(protected axios: AxiosInstance) {}

  public async call(
    data: D,
    options?: RawAxiosRequestConfig,
  ): Promise<AxiosResponse<R, any>> {
    let route = this.route;
    const useMockApi = this.axios.defaults.useMockApi;

    this.params.forEach((param) => {
      const paramValue = data[param as keyof D];
      const paramData =
        paramValue !== undefined && paramValue !== null
          ? encodeURIComponent(paramValue as string)
          : "";
      route = route.replace(`{${param}}`, paramData);
      delete data[param as keyof D];
    });

    const axiosArgs: RequestArgs = {
      url: useMockApi
        ? "/mock" +
          (route.includes("?")
            ? route.split("?")[0] + ".json?" + route.split("?")[1]
            : route + ".json")
        : route,
      hasFile: this.hasFile,
      displayError: this.displayError,
      options: {
        ...options,
        method: useMockApi ? RequestMethod.GET : this.method,
        // For GET requests, move data to params
        ...(this.method === RequestMethod.GET && !useMockApi
          ? { params: data }
          : { data }),
      },
    };

    const request = createRequestFunction(axiosArgs);

    if (useMockApi) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    return request<R>(this.axios);
  }
}
